import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, DataTable, Skeleton } from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import type { CurrencyCode } from '@bbpos/types';
import { formatPaise } from '@/utils/currency';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { dashboardService } from '@/services/dashboard.service';
import { useDashboardStore } from '@/stores/use-dashboard-store';
import { todayIn, useStoreTimezone } from '@/lib/store-clock';
import { usePlanFeature } from '@/hooks/use-plan-feature';
import { LockedFeature } from '@/components/features/subscriptions/LockedFeature';
import type { TopProductItem } from '@/services/dashboard.service';

function makeColumns(currency: CurrencyCode): ColumnDef<TopProductItem & { id: string }>[] {
  return [
    {
      key: 'product_name',
      header: 'Product',
      width: '50%',
    },
    {
      key: 'quantity_sold',
      header: 'Qty',
      numeric: true,
      width: '20%',
    },
    {
      key: 'revenue',
      header: 'Revenue',
      numeric: true,
      width: '30%',
      render: (value) => formatPaise(value as number, currency),
    },
  ];
}

export function TopProductsWidget() {
  const currency = useTenantCurrency();
  const columns = useMemo(() => makeColumns(currency), [currency]);
  const locationId = useDashboardStore((s) => s.selectedLocationId);
  // Today on the store's clock — the tenant's for all locations (T18 / T22), not UTC's.
  const today = todayIn(useStoreTimezone(locationId));
  // /bills/reports/top-products is gated on advanced_reports — a plan without it
  // shows the lock, never "No product sales today" (plan §7, T2-011).
  const hasFeature = usePlanFeature('advanced_reports');

  const { data, isLoading } = useQuery({
    queryKey: ['dashboard', 'top-products', locationId, today],
    queryFn: () => dashboardService.getTopProducts(locationId, 5, today),
    staleTime: 30_000,
    enabled: hasFeature,
  });

  const tableData = (data?.items ?? []).map((item) => ({
    ...item,
    id: item.product_id,
  }));

  if (isLoading) {
    return (
      <Card className="p-4 lg:p-6 shadow-md backdrop-blur-sm bg-white/80 dark:bg-neutral-900/80">
        <Skeleton variant="text" width="40%" height={16} />
        <div className="mt-4 space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} variant="rect" height={32} />
          ))}
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-4 lg:p-6 shadow-md backdrop-blur-sm bg-white/80 dark:bg-neutral-900/80 transition-shadow duration-200 hover:shadow-lg animate-slide-up">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-brand-500 to-indigo-500 flex items-center justify-center text-white">
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
          </svg>
        </div>
        <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">
          Top 5 Products Today
        </h3>
      </div>
      {hasFeature ? (
        <DataTable
          columns={columns}
          data={tableData}
          variant="compact"
          loading={isLoading}
          emptyState={{
            title: 'No product sales today',
            description: 'Product rankings appear after sales are recorded',
          }}
        />
      ) : (
        <LockedFeature feature="advanced_reports" />
      )}
    </Card>
  );
}
